import {
  BackendErrorList,
  CalculatedCostSell,
  CreateOrderResponse,
  Currency,
  Ensure,
  getFormatter,
  InstrumentCategoryEnum,
  isDefined,
  Keys,
  ListedInstrument,
  Loadable,
  OrderResponseType,
  OrderType,
  TradeType,
} from '@eih-netbroker/common';
import { AxiosError, isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { useSnackbar } from '../../../../context/SnackbarContext';
import { Config } from '../../../../shared/config/config';
import { useInventory } from '../../../../shared/services/inventory/useInventory';
import { useEStream } from '../../../../shared/services/sse/useEStream';
import { useAccountBalance } from '../../../../shared/services/useAccountBalance';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { useInstrumentSearch } from '../../../../shared/services/useInstrumentSearch';
import { useOpenOrders } from '../../../../shared/services/useOpenOrders';
import { saveDownloadedFile } from '../../../../shared/utils/fileUtils';
import { useDebounce } from '../../../../shared/utils/useDebounce';
import { useOrderCurrencyExchangeState } from '../../components/OrderCurrencyExchangeStateContext';
import { useOrderState } from '../../components/OrderStateContext';
import { OrderFormData } from '../../components/order/OrderForm';
import { useNormalOrderSchema } from '../../schemas/orderSchemas';
import { withOrderPriceForExanteCostPdf } from '../../utils/orderUtils';
import { OrderPriceValidationError, validateOrderPrice } from './orderPriceValidation';
import { useFuturesInfo } from './useFuturesInfo';
import { useGlobalOrderRequestLoading } from './useGlobalOrderRequestLoading';
import { useOrderClientValidation } from './useOrderClientValidation';
import { useOrderConfirmCalculateCost } from './useOrderConfirmCalculateCost';
import { useOrderManagement } from './useOrderManagement';

export type ValidatedOrder = OrderFormData &
  Ensure<
    Required<
      Pick<
        OrderFormData,
        | 'orderType'
        | 'quantity'
        | 'expiryType'
        | 'tradeType'
        | 'instrument'
        | 'tradingSession'
        | 'currencyExchangeParams'
      >
    >,
    'instrument'
  > & {
    currencyExchangeParams: Required<OrderFormData['currencyExchangeParams']>;
  };

export type ValidatedOrderWithPrice = ValidatedOrder & {
  price: number;
};

type PriceValidationResult = {
  errorMessage?: string;
  warningMessage?: string;
};

const oldErrorFindFunction = (error: { errorCode: string }) =>
  error.errorCode === Keys.ErrorCode.twoFactor.invalidSmsCode ||
  error.errorCode === Keys.ErrorCode.twoFactor.orderSmsConfirmationError;

const newErrorFindFunction = (error: { errorCode: string }) =>
  error.errorCode === Keys.ErrorCode.twoFactor.invalidSmsCode ||
  error.errorCode === Keys.ErrorCode.twoFactor.orderSmsConfirmationError ||
  error.errorCode === Keys.ErrorCode.twoFactor.expiredSmsCode;

export const useCreateNormalOrder = () => {
  const { isGlobalOrderRequestLoading: isLoading, setIsGlobalOrderRequestLoading: setIsLoading } =
    useGlobalOrderRequestLoading();
  const [isMaxQuantityLoading, setIsMaxQuantityLoading] = useState<boolean>(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const createOrderAbortController = useRef<AbortController | undefined>(undefined);
  const {
    mapOrderFormToOrderModifyRequest,
    mapOrderFormToOrderRequest,
    createOrder: _createOrder,
    orderConfirmation,
    modifyOrder: _modifyOrder,
    exportPdfFile,
    generateConfirmationCode,
    loadBalanceForOrder,
  } = useOrderManagement();
  const { loadOrders } = useOpenOrders();
  const { translation, translateWithParams } = useLocale();
  const { showError, showSuccess } = useSnackbar();
  const { validateBeforeCreateOrder } = useOrderClientValidation();
  const { getTickSizeForInstrument, getTickSizeForInstrumentAndPrice } = useInstrumentSearch();
  const { getInstrumentStoreItem, loadInstrumentSingle } = useEStream();
  const { formatCurrencyRoundOrF2, formatCurrencyMaxF10 } = getFormatter();
  const { getSelectedAccount } = useAccounts();
  const { getCashInfoForCurrency } = useAccountBalance();
  const { getInventoryCount } = useInventory();
  const { getNormalOrderSchema } = useNormalOrderSchema();
  const [priceValidationState, setPriceValidationState] = useState<PriceValidationResult>({});
  const [stopActivationPriceValidationState, setStopActivationPriceValidationState] = useState<PriceValidationResult>(
    {},
  );
  const { setExchangeRate } = useOrderCurrencyExchangeState();
  const { setSmsCodeError, setWarnings, transactionId, tempOrder, setAvailableBalance, getOrderPrice } =
    useOrderState();
  const { isLoading: priceValidationLoading, debounce } = useDebounce(setPriceValidationState);
  const { isLoading: stopActivationPriceValidationLoading, debounce: stopActivationPriceValidationDebounce } =
    useDebounce(setStopActivationPriceValidationState);
  const { calculateOrderCost } = useOrderConfirmCalculateCost();
  const { futuresInitialMargin } = useFuturesInfo();

  const generateSmsCode = useCallback(async () => {
    if (tempOrder) {
      await generateConfirmationCode(tempOrder.tempOrderId, transactionId);
    }
  }, [generateConfirmationCode, tempOrder, transactionId]);

  const getAvailableBalance = useCallback(
    async (instrument: ListedInstrument) => {
      try {
        setAvailableBalance(Loadable.loading);
        const response = await loadBalanceForOrder(instrument.currency as Currency, instrument.mic);
        setAvailableBalance(Loadable.loaded(response.investableAmount));
      } catch (error) {
        setAvailableBalance(Loadable.failed);
      }
    },
    [loadBalanceForOrder, setAvailableBalance],
  );

  /**
   * Creates a new order.
   * @param instrument The instrument for the order.
   * @param order The validated order details.
   * @param accountId The selected account for the order.
   * @param validate Whether to revalidate the order before creation.
   * @returns The response from the order creation or undefined if aborted.
   */
  const createOrder = useCallback(
    async (
      instrument: ListedInstrument,
      order: ValidatedOrder,
      accountId: string,
      validate: boolean = false,
    ): Promise<CreateOrderResponse | undefined> => {
      createOrderAbortController.current?.abort();
      const abortController = new AbortController();
      createOrderAbortController.current = abortController;

      try {
        setIsLoading(true);

        if (validate) {
          const validationMessages = await validateBeforeCreateOrder(order, accountId);
          // Check if the request has been aborted during validation before proceeding
          if (abortController.signal.aborted) {
            return undefined;
          }
          if (validationMessages.length > 0) {
            return {
              $type: OrderResponseType.Warning,
              messages: validationMessages,
            };
          }
        }

        const orderRequest = mapOrderFormToOrderRequest({ ...order, instrument });
        return await _createOrder(orderRequest, accountId, transactionId, abortController.signal);
      } catch (e) {
        if (isAxiosError(e) && e.code === AxiosError.ERR_CANCELED) {
          return undefined;
        }
        showError(e);
        throw e;
      } finally {
        if (createOrderAbortController.current === abortController) {
          createOrderAbortController.current = undefined;
          setIsLoading(false);
        }
      }
    },
    [setIsLoading, mapOrderFormToOrderRequest, _createOrder, transactionId, validateBeforeCreateOrder, showError],
  );

  const getPriceValidationErrorMessage = useCallback(
    (
      validationError: OrderPriceValidationError | undefined,
      instrument: ListedInstrument,
      lowerLimitPrice: number,
      upperLimitPrice: number,
      tickSize: number,
    ): string | undefined => {
      switch (validationError) {
        case OrderPriceValidationError.Zero:
          return translateWithParams(translation.orderModal.priceIsZero, {
            price: formatCurrencyRoundOrF2(0, instrument.currency),
          });
        case OrderPriceValidationError.TooLow:
          return translateWithParams(translation.orderModal.priceIsTooLow, {
            price: formatCurrencyRoundOrF2(lowerLimitPrice, instrument.currency),
          });
        case OrderPriceValidationError.TooHigh:
          return translateWithParams(translation.orderModal.priceIsTooHigh, {
            price: formatCurrencyRoundOrF2(upperLimitPrice, instrument.currency),
          });
        case OrderPriceValidationError.TickSize:
          return translateWithParams(translation.orderModal.priceHasInvalidTickSize, {
            tickSize: formatCurrencyMaxF10(tickSize, instrument.currency),
          });
        case OrderPriceValidationError.StopWouldActivateImmediately:
          return translation.orderModal.stopWouldActivateImmediately;
        default:
          return undefined;
      }
    },
    [formatCurrencyRoundOrF2, formatCurrencyMaxF10, translateWithParams, translation.orderModal],
  );

  const validatePrice = useCallback(
    async (instrument: ListedInstrument, price: number, tradeType?: TradeType): Promise<PriceValidationResult> => {
      if (price === 0) {
        return {
          errorMessage: getPriceValidationErrorMessage(OrderPriceValidationError.Zero, instrument, 0, 0, 0),
        };
      }

      let tickSizes: Awaited<ReturnType<typeof getTickSizeForInstrument>>;
      try {
        tickSizes = await getTickSizeForInstrument(instrument);
      } catch {
        return { warningMessage: translation.orderModal.priceValidationWarningWithoutTickSize };
      }

      const lowerLimitPrice = tickSizes[0]?.lowerLimitPrice;
      const upperLimitPrice = tickSizes[tickSizes.length - 1]?.upperLimitPrice;
      if (!isDefined(lowerLimitPrice) || !isDefined(upperLimitPrice)) {
        return { warningMessage: translation.orderModal.priceValidationWarningWithoutTickSize };
      }

      const boundsValidationError = validateOrderPrice({ price, lowerLimitPrice, upperLimitPrice });
      if (isDefined(boundsValidationError)) {
        return {
          errorMessage: getPriceValidationErrorMessage(
            boundsValidationError,
            instrument,
            lowerLimitPrice,
            upperLimitPrice,
            0,
          ),
        };
      }

      let tickSize: Awaited<ReturnType<typeof getTickSizeForInstrumentAndPrice>>;
      try {
        tickSize = await getTickSizeForInstrumentAndPrice(instrument, price);
      } catch {
        return { warningMessage: translation.orderModal.priceValidationWarningWithoutTickSize };
      }

      try {
        let currentMarketPrice: number | undefined;
        if (isDefined(tradeType)) {
          currentMarketPrice = getInstrumentStoreItem(instrument)?.price?.value;
          if (!isDefined(currentMarketPrice)) {
            currentMarketPrice = (await loadInstrumentSingle(instrument)).price?.value;
          }
        }

        return {
          errorMessage: getPriceValidationErrorMessage(
            validateOrderPrice({
              price,
              lowerLimitPrice,
              upperLimitPrice,
              tickSize,
              tradeType,
              currentMarketPrice,
            }),
            instrument,
            lowerLimitPrice,
            upperLimitPrice,
            tickSize,
          ),
        };
      } catch {
        return { errorMessage: translation.orderModal.priceValidationErrorWithoutTickSize };
      }
    },
    [
      getInstrumentStoreItem,
      getPriceValidationErrorMessage,
      getTickSizeForInstrument,
      getTickSizeForInstrumentAndPrice,
      loadInstrumentSingle,
      translation,
    ],
  );

  const validateLimitPrice = useCallback(
    async (order: OrderFormData) => {
      debounce(async () => {
        if (isDefined(order.price) && order.instrument) {
          return await validatePrice(order.instrument, order.price);
        }
        return {};
      });
    },
    [debounce, validatePrice],
  );

  const validateStopActivationPrice = useCallback(
    async (order: OrderFormData) => {
      stopActivationPriceValidationDebounce(async () => {
        if (isDefined(order.stopActivationPrice) && order.instrument) {
          return await validatePrice(order.instrument, order.stopActivationPrice, order.tradeType);
        }
        return {};
      });
    },
    [stopActivationPriceValidationDebounce, validatePrice],
  );

  const modifyOrder = useCallback(
    async (
      orderId: string,
      instrument: ListedInstrument,
      order: ValidatedOrder,
      accountId: string,
      validate: boolean = false,
    ): Promise<CreateOrderResponse> => {
      try {
        setIsLoading(true);

        if (validate) {
          const validationMessages = await validateBeforeCreateOrder(order, accountId);
          if (validationMessages.length > 0) {
            return {
              $type: OrderResponseType.Warning,
              messages: validationMessages,
            };
          }
        }

        const orderRequest = mapOrderFormToOrderModifyRequest({ ...order, instrument });
        return await _modifyOrder({ orderModifyRequest: orderRequest, orderId }, accountId, transactionId);
      } catch (e) {
        showError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, mapOrderFormToOrderModifyRequest, _modifyOrder, transactionId, validateBeforeCreateOrder, showError],
  );

  // isOrderExchange is temporary flag
  // after NN-5082 is fixed, we can remove it
  const confirmOrder = useCallback(
    async (tempOrderId: string, smsConfirmationCode?: string, isModify?: boolean, isOrderExchange?: boolean) => {
      try {
        setIsLoading(true);
        const response = await orderConfirmation(
          {
            orderConfirmationRequest: {
              tempOrderId,
              smsConfirmationCode,
            },
          },
          transactionId,
        );
        showSuccess(isModify ? translation.orderModal.modifyOrderSuccess : translation.orderModal.createOrderSuccess);

        // after NN-5082 is fixed, we can remove the temporary check for isOrderExchange
        if (isOrderExchange) {
          showSuccess(translation.orderModal.orderExchangeSuccess);
        }
        loadOrders(false);
        return response;
      } catch (err) {
        const axiosError = err as AxiosError;
        if (isAxiosError(err) && (axiosError.response?.data as BackendErrorList)?.errors.length > 0) {
          const backendError = axiosError.response?.data as BackendErrorList;
          const findFunction = Config.FeatureFlags.isNewOrderFormEnabled ? newErrorFindFunction : oldErrorFindFunction;
          const smsCodeError = backendError.errors.find(findFunction);
          if (smsCodeError) {
            setSmsCodeError(smsCodeError);
            if (smsCodeError.errorCode === Keys.ErrorCode.twoFactor.orderSmsConfirmationError) {
              // we need to reset the exchange rate if the SMS code is invalid
              setExchangeRate(Loadable.uninitialized);
            }
          } else {
            setWarnings(backendError.errors.map(e => ({ msgType: 'error', msg: e.errorMessage })));
          }
        } else {
          showError(err);
        }

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoading,
      orderConfirmation,
      showError,
      showSuccess,
      translation,
      loadOrders,
      setSmsCodeError,
      setWarnings,
      transactionId,
      setExchangeRate,
    ],
  );

  const getPriceForQuantity = useCallback(
    async (order: OrderFormData): Promise<number | undefined> => {
      const { instrument, tradeType, orderType } = order;
      if (!instrument || !tradeType || !orderType) {
        return undefined;
      }

      if (instrument.instrumentCategory === InstrumentCategoryEnum.Futures) {
        return futuresInitialMargin.isLoaded ? futuresInitialMargin.futuresInitialMargin : undefined;
      } else {
        return await getOrderPrice({
          ...order,
          quantity: 0,
          orderType,
          instrument,
        });
      }
    },
    [futuresInitialMargin, getOrderPrice],
  );

  const getMaxQuantity = useCallback(
    async (order: OrderFormData): Promise<number | undefined> => {
      const { instrument, tradeType, orderType } = order;
      if (!instrument || !tradeType || !orderType) {
        return undefined;
      }

      try {
        setIsMaxQuantityLoading(true);
        const account = await getSelectedAccount();
        if (account && order.instrument) {
          if (order.tradeType === TradeType.Buy) {
            if (
              order.instrument.instrumentCategory === InstrumentCategoryEnum.Futures &&
              !futuresInitialMargin.isLoaded
            ) {
              setIsMaxQuantityLoading(false);
              return undefined;
            }

            const cashInfo = getCashInfoForCurrency(
              {
                account: account,
                client: undefined,
              },
              order.instrument.currency as Currency,
            );

            if (!cashInfo.isLoaded) {
              setIsMaxQuantityLoading(false);
              return undefined;
            }

            const price = await getPriceForQuantity(order);

            if (price === undefined || price === 0) {
              setIsMaxQuantityLoading(false);
              return undefined;
            }

            const estimatedQuantity = Math.max(Math.floor(cashInfo.cash / price), 0);

            const calculatedCost = await calculateOrderCost(
              instrument,
              {
                ...order,
                quantity: estimatedQuantity,
                price:
                  order.orderType === OrderType.Market || order.orderType === OrderType.StopMarket ? undefined : price,
              },
              account.accountNumber,
            );

            if (!calculatedCost.isLoaded) {
              setIsMaxQuantityLoading(false);
              return undefined;
            }

            const commission = (calculatedCost.calculatedCost as CalculatedCostSell).buyAmount;
            const possibleOverflowAmount = estimatedQuantity * price + commission - cashInfo.cash;

            if (possibleOverflowAmount > 0) {
              const quantityToReduce = Math.ceil(possibleOverflowAmount / price);
              setIsMaxQuantityLoading(false);

              return Math.max(estimatedQuantity - quantityToReduce, 0);
            } else {
              setIsMaxQuantityLoading(false);
              return estimatedQuantity;
            }
          } else if (order.tradeType === TradeType.Sell) {
            const invCount = getInventoryCount(order.instrument.ric, account.accountNumber);
            setIsMaxQuantityLoading(false);
            return invCount.isLoaded ? invCount.value : undefined;
          }
        }
        setIsMaxQuantityLoading(false);
        return undefined;
      } catch (error) {
        console.error('Error fetching max quantity', error);
        setIsMaxQuantityLoading(false);
        return undefined;
      }
    },
    [
      getSelectedAccount,
      futuresInitialMargin.isLoaded,
      getCashInfoForCurrency,
      getPriceForQuantity,
      calculateOrderCost,
      getInventoryCount,
    ],
  );

  const downloadPdfFileForNormalOrder = useCallback(
    async (orderForm: OrderFormData) => {
      const normalOrderSchema = getNormalOrderSchema(orderForm);
      const isValid = await normalOrderSchema.isValid(orderForm, { abortEarly: false });

      if (isValid) {
        setPdfDownloading(true);
        const validatedOrder = orderForm as ValidatedOrder;
        const request = withOrderPriceForExanteCostPdf(mapOrderFormToOrderRequest(validatedOrder));
        try {
          const response = await exportPdfFile(request, transactionId);
          saveDownloadedFile(response, 'export.pdf');
        } catch (error) {
          showError(error);
        }

        setPdfDownloading(false);
      }
    },
    [showError, exportPdfFile, getNormalOrderSchema, mapOrderFormToOrderRequest, transactionId],
  );

  // Cleanup on unmount: abort any in-flight order creation requests, when navigating away
  useEffect(
    () => (): void => {
      if (createOrderAbortController.current) {
        console.log('Aborting in-flight order creation on unmount');
        createOrderAbortController.current.abort();
      }
    },
    [],
  );

  return {
    isLoading,
    setIsLoading,
    isMaxQuantityLoading,
    pdfDownloading,
    createOrder,
    modifyOrder,
    validateBeforeCreateOrder,
    confirmOrder,
    getMaxQuantity,
    downloadPdfFileForNormalOrder,
    validateLimitPrice,
    validateStopActivationPrice,
    generateSmsCode,
    priceValidationState: {
      ...priceValidationState,
      isLoading: priceValidationLoading,
    },
    stopActivationPriceValidationState: {
      ...stopActivationPriceValidationState,
      isLoading: stopActivationPriceValidationLoading,
    },
    getAvailableBalance,
  };
};
